import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Box,
  Tabs,
  Tab,
  Link,
  Alert,
} from '@mui/material';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import { useAuth } from '../contexts/AuthContext';
import { afterSignInPath } from '../utils/safeNext';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { clearError } = useAuth();
  const [searchParams] = useSearchParams();
  // An invitation link opens the sign-up tab with the invited email filled in.
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 1 : 0);
  const presetEmail = searchParams.get('email') ?? undefined;
  const destination = afterSignInPath(location);
  const forInvitation = destination.startsWith('/invitations/');
  // Set by the delete-account dialog after the account is gone.
  const accountDeleted = searchParams.get('deleted') === '1';

  const handleAuthSuccess = () => {
    navigate(destination, { replace: true });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    clearError(); // Clear any existing error when switching tabs
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {accountDeleted && (
          <Alert severity='success' sx={{ mb: 3 }}>
            {t('login.accountDeleted')}
          </Alert>
        )}
        {forInvitation && (
          <Alert severity='info' sx={{ mb: 3 }}>
            {t('login.forInvitation')}
          </Alert>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={handleTabChange} centered>
            <Tab label={t('tabs.login')} />
            <Tab label={t('tabs.register')} />
          </Tabs>
        </Box>

        {tab === 0 ? (
          <LoginForm onSuccess={handleAuthSuccess} defaultEmail={presetEmail} />
        ) : (
          <RegisterForm onSuccess={handleAuthSuccess} defaultEmail={presetEmail} />
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to="/" variant="body2">
            {t('navigation.backToHome')}
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;